import axiosInstance from './axiosInstance'

export interface LobSalesExpiryItem {
  lobId: string
  lobName: string
  days: number
}

// ── Knowlarity ────────────────────────────────────────────────────────────────

export interface KnowlarityNumber {
  id: string
  orgId: string
  lobId: string
  kNumber: string
  callerId: string
  label?: string
  isActive: boolean
  createdAt: string
}

export interface AddKnowlarityNumberPayload {
  orgId: string
  lobId: string
  kNumber: string
  callerId: string
  label?: string
}

// ── API ───────────────────────────────────────────────────────────────────────

export const settingsApi = {
  getSalesExpiry: async (): Promise<LobSalesExpiryItem[]> => {
    const res = await axiosInstance.get<{ data: LobSalesExpiryItem[] }>('/settings/sales-expiry')
    return res.data.data
  },

  updateSalesExpiry: async (payload: { lobId: string; days: number }): Promise<{ lobId: string; days: number }> => {
    const res = await axiosInstance.post<{ data: { lobId: string; days: number } }>('/settings/sales-expiry', payload)
    return res.data.data
  },

  // List all numbers for a specific org + lob
  getKnowlarityNumbers: async (orgId: string, lobId: string): Promise<KnowlarityNumber[]> => {
    const res = await axiosInstance.get<{ data: KnowlarityNumber[] }>('/settings/knowlarity', {
      params: { orgId, lobId }
    })
    return res.data.data
  },

  // Add a new number entry
  addKnowlarityNumber: async (payload: AddKnowlarityNumberPayload): Promise<KnowlarityNumber> => {
    const res = await axiosInstance.post<{ data: KnowlarityNumber }>('/settings/knowlarity', payload)
    return res.data.data
  },

  // Permanently delete a number
  deleteKnowlarityNumber: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/settings/knowlarity/${id}`)
  },

  // Toggle active/inactive
  toggleKnowlarityNumber: async (id: string): Promise<{ id: string; isActive: boolean }> => {
    const res = await axiosInstance.patch<{ data: { id: string; isActive: boolean } }>(
      `/settings/knowlarity/${id}/toggle`
    )
    return res.data.data
  },

  // Retention Customer telephony mapping
  getRetentionKnowlaritySetting: async (): Promise<{ orgId: string | null; lobId: string | null }> => {
    const res = await axiosInstance.get<{ data: { orgId: string | null; lobId: string | null } }>('/settings/knowlarity/retention')
    return res.data.data
  },

  updateRetentionKnowlaritySetting: async (payload: { orgId: string | null; lobId: string | null }): Promise<{ orgId: string | null; lobId: string | null }> => {
    const res = await axiosInstance.post<{ data: { orgId: string | null; lobId: string | null } }>('/settings/knowlarity/retention', payload)
    return res.data.data
  },

  // Number Rotation
  getKnowlarityRotation: async (orgId: string, lobId: string): Promise<RotationConfig> => {
    const res = await axiosInstance.get<{ data: RotationConfig }>('/settings/knowlarity/rotation', {
      params: { orgId, lobId }
    })
    return res.data.data
  },

  updateKnowlarityRotation: async (orgId: string, lobId: string, frequencyHours: number): Promise<{ frequencyHours: number }> => {
    const res = await axiosInstance.post<{ data: { frequencyHours: number } }>('/settings/knowlarity/rotation', { frequencyHours }, {
      params: { orgId, lobId }
    })
    return res.data.data
  },

  overrideKnowlarityRotation: async (orgId: string, lobId: string, numberId: string): Promise<void> => {
    await axiosInstance.post('/settings/knowlarity/rotation/override', { numberId }, {
      params: { orgId, lobId }
    })
  }
}

export interface RotationConfig {
  frequencyHours: number
  baseIndex: number
  epochStart: string
  activeNumber: KnowlarityNumber | null
  nextRotationAt: string
}
